import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router';
import { navigateRequireAuth } from '../../utils/authRedirect';
import { toast } from 'react-toastify';
import styled, { keyframes } from 'styled-components';
import { designerItemsAPI } from '../../api/designerItemsAPI';
import {
  trackShopCreateStarted,
  trackShopCreateSucceeded,
  trackShopCreateFailed,
} from '../../utils/analytics';
import { requestIdFromError } from '../../utils/requestId';
import ListingMediaUploadField from '../../components/items/ListingMediaUploadField';
import { uploadShopCreateCatalog } from '../../utils/uploadShopCreateCatalog';
import {
  isAlreadyHaveShopError,
  normalizeShopApprovalStatus,
} from '../../utils/shopOwnerPortal';

/* ─── Animations ───────────────────────────────────────────────────── */
const fadeIn = keyframes`from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: none; }`;

/* ─── Layout ────────────────────────────────────────────────────────── */
const Page = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #ec4899 0%, #be185d 100%);
  padding: 2rem 1rem 5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Card = styled.div`
  width: 100%;
  max-width: 680px;
  background: white;
  border-radius: 1.25rem;
  box-shadow: 0 20px 60px rgba(0,0,0,0.18);
  overflow: hidden;
  animation: ${fadeIn} 0.4s ease;
`;

const CardHeader = styled.div`
  background: linear-gradient(135deg, #ec4899 0%, #be185d 100%);
  padding: 2rem 2rem 1.5rem;
  color: white;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 800;
  margin: 0 0 0.35rem;
`;

const Subtitle = styled.p`
  font-size: 0.95rem;
  opacity: 0.85;
  margin: 0;
`;

/* ─── Progress bar ─────────────────────────────────────────────────── */
const ProgressWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
`;

const StepCircle = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 700;
  transition: all 0.3s;
  background: ${p => p.$done ? '#10b981' : p.$active ? '#be185d' : '#e5e7eb'};
  color: ${p => p.$done || p.$active ? 'white' : '#9ca3af'};
  flex-shrink: 0;
`;

const StepLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${p => p.$active ? '#be185d' : '#9ca3af'};
  margin-top: 0.35rem;
  white-space: nowrap;
`;

const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
`;

const Connector = styled.div`
  height: 2px;
  width: 48px;
  background: ${p => p.$done ? '#10b981' : '#e5e7eb'};
  flex-shrink: 0;
  margin-bottom: 1.3rem;
  transition: background 0.3s;
`;

/* ─── Form area ────────────────────────────────────────────────────── */
const FormArea = styled.div`
  padding: 2rem;
`;

const StepTitle = styled.h2`
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0.35rem;
`;

const StepDesc = styled.p`
  font-size: 0.9rem;
  color: #64748b;
  margin: 0 0 1.5rem;
`;

const Group = styled.div`
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.4rem;
`;

const Required = styled.span`color: #ef4444; margin-left: 2px;`;
const Optional = styled.span`color: #9ca3af; font-weight: 400; font-size: 0.8rem; margin-left: 4px;`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1.5px solid ${p => p.$error ? '#ef4444' : '#d1d5db'};
  border-radius: 0.5rem;
  font-size: 0.95rem;
  transition: border-color 0.2s;
  box-sizing: border-box;
  &:focus { outline: none; border-color: #be185d; }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1.5px solid ${p => p.$error ? '#ef4444' : '#d1d5db'};
  border-radius: 0.5rem;
  font-size: 0.95rem;
  resize: vertical;
  min-height: ${p => p.$tall ? '130px' : '100px'};
  transition: border-color 0.2s;
  box-sizing: border-box;
  font-family: inherit;
  &:focus { outline: none; border-color: #be185d; }
`;

const ErrorMsg = styled.p`
  font-size: 0.8rem;
  color: #ef4444;
  margin: 0.3rem 0 0;
`;

const CharCount = styled.p`
  font-size: 0.75rem;
  color: ${p => p.$over ? '#ef4444' : '#9ca3af'};
  margin: 0.25rem 0 0;
  text-align: right;
`;

/* ─── Logo upload ──────────────────────────────────────────────────── */
const LogoUploadArea = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
`;

const LogoPreview = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 2px dashed ${p => p.$hasImage ? '#be185d' : '#d1d5db'};
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  cursor: pointer;
  transition: border-color 0.2s;
  &:hover { border-color: #be185d; }
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const LogoUploadBtn = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.1rem;
  background: #fdf2f8;
  color: #be185d;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background: #fce7f3; }
`;

/* ─── Navigation buttons ───────────────────────────────────────────── */
const NavRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 2rem;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
`;

const BtnBack = styled.button`
  padding: 0.7rem 1.5rem;
  border: 1.5px solid #d1d5db;
  border-radius: 0.5rem;
  background: white;
  color: #374151;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { border-color: #be185d; color: #be185d; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const BtnNext = styled.button`
  padding: 0.7rem 1.75rem;
  border: none;
  border-radius: 0.5rem;
  background: linear-gradient(135deg, #ec4899 0%, #be185d 100%);
  color: white;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.15s;
  &:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

/* ─── Review step ──────────────────────────────────────────────────── */
const ReviewBox = styled.div`
  background: #f8f9fa;
  border-radius: 0.75rem;
  padding: 1.25rem;
  margin-bottom: 1rem;
`;

const ReviewLabel = styled.p`
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #9ca3af;
  margin: 0 0 0.3rem;
  letter-spacing: 0.05em;
`;

const ReviewValue = styled.p`
  font-size: 0.95rem;
  color: #1e293b;
  margin: 0;
  white-space: pre-wrap;
  line-height: 1.5;
`;

const StatusNote = styled.div`
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 0.5rem;
  padding: 0.9rem 1rem;
  font-size: 0.875rem;
  color: #92400e;
  margin-top: 1rem;
`;

/* ─── Catalog step ─────────────────────────────────────────────────── */
const CatalogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const CatalogCard = styled.div`
  background: #f8f9fa;
  border-radius: 0.75rem;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  position: relative;
`;

const CatalogCardImg = styled.img`
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 0.5rem;
  margin-bottom: 0.5rem;
`;

const CatalogCardName = styled.p`
  font-weight: 600;
  font-size: 0.9rem;
  color: #1e293b;
  margin: 0 0 0.25rem;
`;

const CatalogCardPrice = styled.p`
  font-size: 0.85rem;
  color: #10b981;
  font-weight: 700;
  margin: 0;
`;

const RemoveCatalogBtn = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.9);
  color: white;
  border: none;
  cursor: pointer;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: #ef4444; }
`;

const AddItemForm = styled.div`
  background: #f0fdf4;
  border: 1px dashed #a7f3d0;
  border-radius: 0.75rem;
  padding: 1.25rem;
  margin-bottom: 1rem;
`;

/* ─── Component ────────────────────────────────────────────────────── */
const STEPS = [
  { label: 'Service', icon: '💍' },
  { label: 'Policies', icon: '📋' },
  { label: 'Catalog', icon: '📦' },
  { label: 'Review', icon: '✅' },
];

const ProvideWeddingServicePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useSelector(s => s.auth);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [finishingUploads, setFinishingUploads] = useState(false);
  const [checkingExisting, setCheckingExisting] = useState(true);

  const [form, setForm] = useState({
    service_name: '',
    service_description: '',
    instagram_handle: '',
    social_link: '',
    return_policy: '',
    shipping_policy: '',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [catalogItems, setCatalogItems] = useState([]);
  const catalogItemsRef = useRef(catalogItems);
  const [catalogForm, setCatalogForm] = useState({ name: '', price: '', description: '' });
  const [catalogMedia, setCatalogMedia] = useState([]);
  const catalogMediaUploadApiRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) { navigateRequireAuth(navigate, location); return; }
    (async () => {
      try {
        const shop = await designerItemsAPI.getMyShop();
        if (shop && shop.id && shop.category === 'wedding') {
          navigate(`/designer-shop/${designerItemsAPI.buildShopRef(shop)}`, { replace: true });
        }
      } catch {
        // 404 = no shop yet
      } finally {
        setCheckingExisting(false);
      }
    })();
  }, [isAuthenticated, navigate, location]);

  const update = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    if (errors[field]) setErrors(e => ({ ...e, [field]: '' }));
  };

  const handleLogoChange = (e) => {
    const MAX_FILE_SIZE = 10 * 1024 * 1024;
    const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error(`"${file.name}" has an unsupported format. Use JPEG, PNG, GIF, or WebP.`);
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`"${file.name}" is too large (max 10MB).`);
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const validateStep0 = () => {
    const errs = {};
    if (!form.service_name.trim()) errs.service_name = 'Service name is required.';
    else if (form.service_name.trim().length < 2) errs.service_name = 'Service name must be at least 2 characters.';
    if (form.service_description.length > 600) errs.service_description = 'Description must be under 600 characters.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateStep1 = () => {
    const errs = {};
    if (form.return_policy.trim() && form.return_policy.length > 1000)
      errs.return_policy = 'Return policy must be under 1000 characters.';
    if (form.shipping_policy.trim() && form.shipping_policy.length > 1000)
      errs.shipping_policy = 'Shipping policy must be under 1000 characters.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const catalogDraftStarted = () => Boolean(
    catalogForm.name.trim()
    || catalogForm.price
    || catalogForm.description.trim()
    || catalogMedia.length > 0
  );

  const commitCatalogItem = async () => {
    if (!catalogForm.name.trim()) {
      toast.error('Item name is required.');
      return false;
    }
    if (!catalogForm.price || Number(catalogForm.price) <= 0) {
      toast.error('Price must be greater than 0.');
      return false;
    }
    if (catalogMedia.length === 0) {
      toast.error('Add at least one image for this item.');
      return false;
    }
    setFinishingUploads(true);
    try {
      const outcomes = catalogMediaUploadApiRef.current
        ? await catalogMediaUploadApiRef.current.waitForUploads()
        : catalogMedia.map(() => null);
      const failedCount = outcomes.filter((o) => !o || (!o.key && !o.deferred)).length;
      if (failedCount > 0) {
        toast.error(
          `${failedCount} photo/video failed to upload. Tap Retry on the failed thumbnail, then try again.`,
        );
        return false;
      }
      const nextItem = {
        name: catalogForm.name.trim(),
        price: catalogForm.price,
        description: catalogForm.description.trim(),
        images: catalogMedia.map((m, i) => ({
          file: m.file,
          ...(outcomes[i]?.key ? { pendingKey: outcomes[i].key } : {}),
        })),
        previews: catalogMedia.map((m) => m.preview),
      };
      const next = [...catalogItemsRef.current, nextItem];
      catalogItemsRef.current = next;
      setCatalogItems(next);
      setCatalogForm({ name: '', price: '', description: '' });
      setCatalogMedia([]);
      return next;
    } finally {
      setFinishingUploads(false);
    }
  };

  const addCatalogItem = async () => {
    await commitCatalogItem();
  };

  const removeCatalogItem = (index) => {
    const next = catalogItemsRef.current.filter((_, i) => i !== index);
    catalogItemsRef.current = next;
    setCatalogItems(next);
  };

  const resolveCatalogItems = () => {
    if (catalogDraftStarted()) return commitCatalogItem();
    return catalogItemsRef.current;
  };

  const handleNext = async () => {
    if (step === 0 && !validateStep0()) return;
    if (step === 1 && !validateStep1()) return;
    if (step === 2) {
      const items = await resolveCatalogItems();
      if (!items) return;
      if (items.length === 0) {
        toast.error('Add at least one catalog item before proceeding.');
        return;
      }
    }
    setStep(s => s + 1);
  };

  const handleSubmit = async () => {
    const items = await resolveCatalogItems();
    if (!items || items.length === 0) {
      toast.error('Add at least one catalog item before submitting.');
      setStep(2);
      return;
    }
    setSubmitting(true);
    const analyticsBase = {
      shop_type: 'wedding',
      catalog_item_count: items.length,
      has_logo: Boolean(logoFile),
    };
    trackShopCreateStarted(analyticsBase);
    let stage = 'shop';
    let shopAlreadyExisted = false;
    let shopId;
    try {
      const fd = new FormData();
      fd.append('brand_name', form.service_name.trim());
      fd.append('bio', form.service_description.trim());
      fd.append('instagram_handle', form.instagram_handle.trim());
      fd.append('social_link', form.social_link.trim());
      fd.append('return_policy', form.return_policy.trim());
      fd.append('shipping_policy', form.shipping_policy.trim());
      fd.append('category', 'wedding');
      if (logoFile) fd.append('logo', logoFile);
      try {
        const created = await designerItemsAPI.createShop(fd);
        shopId = created?.id;
      } catch (shopErr) {
        const msg = String(shopErr?.response?.data?.error || shopErr?.message || '');
        if (!isAlreadyHaveShopError(msg)) {
          throw shopErr;
        }
        shopAlreadyExisted = true;
        try {
          const existing = await designerItemsAPI.getMyShop();
          const approval = normalizeShopApprovalStatus(existing?.approval_status);
          if (approval === 'APPROVED' && existing?.id) {
            toast.success('Your wedding service is already approved.');
            trackShopCreateSucceeded({
              ...analyticsBase,
              shop_id: existing.id,
              shop_already_existed: true,
            });
            navigate(`/designer-shop/${designerItemsAPI.buildShopRef(existing)}`, { replace: true });
            return;
          }
          if (approval === 'REJECTED') {
            toast.error('This shop was not approved. Update your details and submit again.');
            return;
          }
        } catch {
          // Pending shops still need catalog upload below.
        }
      }

      stage = 'catalog';

      await uploadShopCreateCatalog({
        kind: 'designer',
        brandName: form.service_name.trim(),
        items: items.map((item) => ({
          ...item,
          category: 'wedding',
        })),
        api: {
          createDraft: designerItemsAPI.createDesignerItemDraft,
          uploadMedia: designerItemsAPI.uploadDesignerItemMedia,
          finalize: designerItemsAPI.finalizeDesignerItemDraft,
          initDirectUpload: designerItemsAPI.initDesignerDirectUpload,
          signDirectParts: designerItemsAPI.signDesignerDirectParts,
          completeDirectUpload: designerItemsAPI.completeDesignerDirectUpload,
          abortDirectUpload: designerItemsAPI.abortDesignerDirectUpload,
          attachPending: designerItemsAPI.attachDesignerPendingImages,
        },
      });

      toast.success('Your wedding service has been submitted for review!');
      trackShopCreateSucceeded({
        ...analyticsBase,
        shop_id: shopId,
        shop_already_existed: shopAlreadyExisted,
      });
      navigate('/shop-pending?type=designer', { replace: true });
    } catch (err) {
      trackShopCreateFailed({
        ...analyticsBase,
        shop_id: shopId,
        shop_already_existed: shopAlreadyExisted,
        stage,
        error: err?.response?.data?.error || err?.message || 'Failed to create service',
        status: err?.response?.status ?? null,
        request_id: requestIdFromError(err),
      });
      const msg = err.response?.data?.error || err?.message || 'Failed to create service. Please try again.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (checkingExisting) {
    return (
      <Page>
        <div style={{ color: 'white', fontSize: '1.1rem', marginTop: '4rem' }}>Loading…</div>
      </Page>
    );
  }

  return (
    <Page>
      <Card>
        <CardHeader>
          <Title>💍 Provide Wedding Service</Title>
          <Subtitle>Showcase your wedding services and reach couples planning their special day</Subtitle>
        </CardHeader>

        {/* Progress indicator */}
        <ProgressWrap>
          {STEPS.map((s, i) => (
            <React.Fragment key={i}>
              <StepItem>
                <StepCircle $active={step === i} $done={step > i}>
                  {step > i ? '✓' : i + 1}
                </StepCircle>
                <StepLabel $active={step === i}>{s.label}</StepLabel>
              </StepItem>
              {i < STEPS.length - 1 && <Connector $done={step > i} />}
            </React.Fragment>
          ))}
        </ProgressWrap>

        {/* Step 0: Service Identity */}
        {step === 0 && (
          <FormArea>
            <StepTitle>Service Identity</StepTitle>
            <StepDesc>Tell couples who you are. Your service name and logo are the first things they see.</StepDesc>

            <Group>
              <Label>Service Name <Required>*</Required></Label>
              <Input
                type="text"
                placeholder="e.g. Rose & Gold Wedding Planning"
                value={form.service_name}
                onChange={e => update('service_name', e.target.value)}
                maxLength={150}
                $error={!!errors.service_name}
              />
              {errors.service_name && <ErrorMsg>{errors.service_name}</ErrorMsg>}
            </Group>

            <Group>
              <Label>Logo <Optional>(optional)</Optional></Label>
              <LogoUploadArea>
                <LogoPreview $hasImage={!!logoPreview}>
                  {logoPreview
                    ? <img src={logoPreview} alt="logo preview" />
                    : <span style={{ fontSize: '2rem' }}>💍</span>}
                </LogoPreview>
                <div>
                  <LogoUploadBtn htmlFor="wedding-logo-input">
                    📷 {logoFile ? 'Change logo' : 'Upload logo'}
                    <input
                      id="wedding-logo-input"
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleLogoChange}
                    />
                  </LogoUploadBtn>
                  <p style={{ fontSize: '0.78rem', color: '#9ca3af', marginTop: '0.4rem' }}>
                    Square image, max 5 MB. PNG or JPEG recommended.
                  </p>
                </div>
              </LogoUploadArea>
            </Group>

            <Group>
              <Label>Service Description <Optional>(optional)</Optional></Label>
              <Textarea
                placeholder="Describe your wedding services — what you offer, your experience, your style…"
                value={form.service_description}
                onChange={e => update('service_description', e.target.value)}
                maxLength={600}
                $error={!!errors.service_description}
              />
              <CharCount $over={form.service_description.length > 600}>{form.service_description.length}/600</CharCount>
              {errors.service_description && <ErrorMsg>{errors.service_description}</ErrorMsg>}
            </Group>

            <Group>
              <Label>Instagram Handle <Optional>(optional)</Optional></Label>
              <Input
                type="text"
                placeholder="@yourservice"
                value={form.instagram_handle}
                onChange={e => update('instagram_handle', e.target.value)}
                maxLength={100}
              />
              <p style={{ fontSize: '0.78rem', color: '#9ca3af', margin: '0.25rem 0 0' }}>
                Shown on your service page and included in the admin review.
              </p>
            </Group>

            <Group>
              <Label>Other Social / Website <Optional>(optional)</Optional></Label>
              <Input
                type="url"
                placeholder="https://tiktok.com/@yourservice or https://yourservice.com"
                value={form.social_link}
                onChange={e => update('social_link', e.target.value)}
                maxLength={500}
              />
            </Group>
          </FormArea>
        )}

        {/* Step 1: Policies */}
        {step === 1 && (
          <FormArea>
            <StepTitle>Service Policies</StepTitle>
            <StepDesc>Clear policies build trust with couples. You can always update these later.</StepDesc>

            <Group>
              <Label>Cancellation / Return Policy <Optional>(optional)</Optional></Label>
              <Textarea
                $tall
                placeholder="e.g. Cancellations accepted up to 30 days before the event with a full refund…"
                value={form.return_policy}
                onChange={e => update('return_policy', e.target.value)}
                maxLength={1000}
                $error={!!errors.return_policy}
              />
              <CharCount $over={form.return_policy.length > 1000}>{form.return_policy.length}/1000</CharCount>
              {errors.return_policy && <ErrorMsg>{errors.return_policy}</ErrorMsg>}
            </Group>

            <Group>
              <Label>Delivery / Service Area Policy <Optional>(optional)</Optional></Label>
              <Textarea
                $tall
                placeholder="e.g. We serve Cairo, Giza, and Alexandria. Travel fees may apply for other governorates…"
                value={form.shipping_policy}
                onChange={e => update('shipping_policy', e.target.value)}
                maxLength={1000}
                $error={!!errors.shipping_policy}
              />
              <CharCount $over={form.shipping_policy.length > 1000}>{form.shipping_policy.length}/1000</CharCount>
              {errors.shipping_policy && <ErrorMsg>{errors.shipping_policy}</ErrorMsg>}
            </Group>
          </FormArea>
        )}

        {/* Step 2: Catalog */}
        {step === 2 && (
          <FormArea>
            <StepTitle>Your Catalog</StepTitle>
            <StepDesc>Add at least one item to showcase your services. Couples want to see what you offer before booking.</StepDesc>

            <AddItemForm>
              <Group>
                <Label>Item Name <Required>*</Required></Label>
                <Input
                  type="text"
                  placeholder="e.g. Full Day Photography Package"
                  value={catalogForm.name}
                  onChange={e => setCatalogForm(f => ({ ...f, name: e.target.value }))}
                  maxLength={150}
                />
              </Group>
              <Group>
                <Label>Price (EGP) <Required>*</Required></Label>
                <Input
                  type="number"
                  placeholder="e.g. 15000"
                  value={catalogForm.price}
                  onChange={e => setCatalogForm(f => ({ ...f, price: e.target.value }))}
                  min="1"
                />
              </Group>
              <Group>
                <Label>Description <Optional>(optional)</Optional></Label>
                <Textarea
                  placeholder="Brief description of this service package…"
                  value={catalogForm.description}
                  onChange={e => setCatalogForm(f => ({ ...f, description: e.target.value }))}
                  maxLength={500}
                />
              </Group>
              <Group>
                <ListingMediaUploadField
                  mediaItems={catalogMedia}
                  onChange={setCatalogMedia}
                  maxMedia={10}
                  minMedia={1}
                  minPhotos={1}
                  minRecommendedPhotos={3}
                  idPrefix="wedding-catalog"
                  label="Photos & videos"
                  pickTimeUpload
                  uploadApiRef={catalogMediaUploadApiRef}
                />
              </Group>
              <BtnNext type="button" onClick={addCatalogItem} style={{ width: '100%' }} disabled={finishingUploads}>
                {finishingUploads ? 'Finishing photo uploads…' : '+ Add Item'}
              </BtnNext>
            </AddItemForm>

            {catalogItems.length > 0 && (
              <CatalogGrid>
                {catalogItems.map((item, i) => (
                  <CatalogCard key={i}>
                    <RemoveCatalogBtn onClick={() => removeCatalogItem(i)}>✕</RemoveCatalogBtn>
                    <CatalogCardImg src={item.previews[0]} alt={item.name} />
                    <CatalogCardName>{item.name}</CatalogCardName>
                    <CatalogCardPrice>{Number(item.price).toLocaleString()} EGP</CatalogCardPrice>
                  </CatalogCard>
                ))}
              </CatalogGrid>
            )}

            {catalogItems.length === 0 && (
              <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: '0.875rem', marginTop: '1rem' }}>
                No items added yet. Add at least one to continue.
              </p>
            )}
          </FormArea>
        )}

        {/* Step 3: Review & submit */}
        {step === 3 && (
          <FormArea>
            <StepTitle>Review Your Service</StepTitle>
            <StepDesc>Everything look good? You can edit your service anytime after it's approved.</StepDesc>

            <ReviewBox>
              <ReviewLabel>Service Name</ReviewLabel>
              <ReviewValue>{form.service_name || '—'}</ReviewValue>
            </ReviewBox>

            {logoPreview && (
              <ReviewBox style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <img src={logoPreview} alt="logo" style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover' }} />
                <div>
                  <ReviewLabel style={{ margin: 0 }}>Logo</ReviewLabel>
                  <ReviewValue style={{ fontSize: '0.85rem' }}>{logoFile?.name}</ReviewValue>
                </div>
              </ReviewBox>
            )}

            {form.service_description && (
              <ReviewBox>
                <ReviewLabel>Description</ReviewLabel>
                <ReviewValue>{form.service_description}</ReviewValue>
              </ReviewBox>
            )}

            {form.instagram_handle && (
              <ReviewBox>
                <ReviewLabel>Instagram</ReviewLabel>
                <ReviewValue>{form.instagram_handle}</ReviewValue>
              </ReviewBox>
            )}

            {form.social_link && (
              <ReviewBox>
                <ReviewLabel>Social / Website</ReviewLabel>
                <ReviewValue>{form.social_link}</ReviewValue>
              </ReviewBox>
            )}

            {form.return_policy && (
              <ReviewBox>
                <ReviewLabel>Cancellation / Return Policy</ReviewLabel>
                <ReviewValue>{form.return_policy}</ReviewValue>
              </ReviewBox>
            )}

            {form.shipping_policy && (
              <ReviewBox>
                <ReviewLabel>Delivery / Service Area Policy</ReviewLabel>
                <ReviewValue>{form.shipping_policy}</ReviewValue>
              </ReviewBox>
            )}

            <ReviewBox>
              <ReviewLabel>Catalog Items</ReviewLabel>
              <ReviewValue>{catalogItems.length} item{catalogItems.length !== 1 ? 's' : ''}</ReviewValue>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                {catalogItems.map((item, i) => (
                  <img key={i} src={item.previews[0]} alt={item.name} style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: '0.375rem' }} />
                ))}
              </div>
            </ReviewBox>

            <StatusNote>
              ⏳ Your wedding service will be reviewed by our team before going live. We'll notify you once it's approved or if changes are needed.
            </StatusNote>

            <BtnNext
              onClick={handleSubmit}
              disabled={submitting || finishingUploads}
              style={{ width: '100%', marginTop: '1.25rem', padding: '0.9rem', fontSize: '1rem' }}
            >
              {finishingUploads ? 'Finishing photo uploads…' : submitting ? 'Submitting…' : '💍 Submit Service for Review'}
            </BtnNext>
          </FormArea>
        )}

        {/* Navigation */}
        <NavRow>
          <BtnBack onClick={() => step === 0 ? navigate('/wedding-essentials') : setStep(s => s - 1)} disabled={submitting}>
            {step === 0 ? '← Back' : '← Previous'}
          </BtnBack>
          {step < 3 ? (
            <BtnNext onClick={handleNext} disabled={finishingUploads}>
              {finishingUploads
                ? 'Finishing photo uploads…'
                : step === 2 && (catalogItems.length === 0 || catalogDraftStarted())
                  ? 'Save & Continue →'
                  : 'Next →'}
            </BtnNext>
          ) : (
            <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>↑ Click the button above</span>
          )}
        </NavRow>
      </Card>
    </Page>
  );
};

export default ProvideWeddingServicePage;
